export type KnowledgeBookVisualizationAvailability =
    | 'available'
    | 'read-only'
    | 'incompatible-ontology';

export interface KnowledgeBookVisualizationTreeOption {
    availability: KnowledgeBookVisualizationAvailability;
    knowledgeBook: Pick<IKnowledgeBook, 'ID' | 'Name' | 'ClassificationTags'>;
}

export interface KnowledgeBookVisualizationTreeNode {
    availability?: KnowledgeBookVisualizationAvailability;
    children: KnowledgeBookVisualizationTreeNode[];
    id: string;
    kind: 'classification' | 'knowledge-book';
    knowledgeBookID?: string;
    name: string;
}

const normalizeID = (value?: string | null) => (value ?? '').toLocaleLowerCase();

export const getKnowledgeBookVisualizationAvailability = (
    knowledgeBook: Pick<IKnowledgeBook, 'OntologyID'>,
    is3DViewer: boolean,
    currentOntologyID: string | null | undefined,
    canModify: boolean,
): KnowledgeBookVisualizationAvailability => {
    if (!canModify) return 'read-only';
    const normalizedCurrentOntologyID = normalizeID(currentOntologyID);
    const normalizedKnowledgeBookOntologyID = normalizeID(knowledgeBook.OntologyID);
    if (is3DViewer && !normalizedCurrentOntologyID) {
        return 'incompatible-ontology';
    }
    if (normalizedKnowledgeBookOntologyID &&
        normalizedKnowledgeBookOntologyID !== normalizedCurrentOntologyID) {
        return 'incompatible-ontology';
    }
    return 'available';
};

export const buildKnowledgeBookVisualizationTree = (
    options: readonly KnowledgeBookVisualizationTreeOption[],
    classificationTags: readonly IClassificationTag[],
    searchText = '',
): KnowledgeBookVisualizationTreeNode[] => {
    const normalizedSearch = searchText.trim().toLocaleLowerCase();
    const matchingOptions = options
        .filter(option => !normalizedSearch || option.knowledgeBook.Name.toLocaleLowerCase().includes(normalizedSearch))
        .sort((left, right) => left.knowledgeBook.Name.localeCompare(
            right.knowledgeBook.Name,
            undefined,
            { sensitivity: 'base' },
        ));
    const tagMap = new Map(classificationTags.map(tag => [tag.ID, tag] as const));
    const childTagsByParentID = new Map<string | null, IClassificationTag[]>();
    const booksByTagID = new Map<string, KnowledgeBookVisualizationTreeOption[]>();
    const rootBooks: KnowledgeBookVisualizationTreeOption[] = [];

    classificationTags.forEach(tag => {
        const parentID = tag.ParentID && tagMap.has(tag.ParentID) ? tag.ParentID : null;
        const siblings = childTagsByParentID.get(parentID) ?? [];
        siblings.push(tag);
        childTagsByParentID.set(parentID, siblings);
    });
    childTagsByParentID.forEach(tags => tags.sort((left, right) =>
        left.Name.localeCompare(right.Name, undefined, { sensitivity: 'base' })
    ));

    matchingOptions.forEach(option => {
        const validTagIDs = [...new Set(option.knowledgeBook.ClassificationTags ?? [])]
            .filter(tagID => tagMap.has(tagID));
        if (validTagIDs.length === 0) {
            rootBooks.push(option);
            return;
        }
        validTagIDs.forEach(tagID => {
            const books = booksByTagID.get(tagID) ?? [];
            books.push(option);
            booksByTagID.set(tagID, books);
        });
    });

    const createBookNode = (
        option: KnowledgeBookVisualizationTreeOption,
        parentID: string,
    ): KnowledgeBookVisualizationTreeNode => ({
        availability: option.availability,
        children: [],
        id: `knowledge-book:${parentID}:${option.knowledgeBook.ID}`,
        kind: 'knowledge-book',
        knowledgeBookID: option.knowledgeBook.ID,
        name: option.knowledgeBook.Name,
    });

    const buildTagNode = (
        tag: IClassificationTag,
        visitedTagIDs: ReadonlySet<string>,
    ): KnowledgeBookVisualizationTreeNode | null => {
        if (visitedTagIDs.has(tag.ID)) return null;
        const nextVisitedTagIDs = new Set(visitedTagIDs).add(tag.ID);
        const childTags = (childTagsByParentID.get(tag.ID) ?? [])
            .map(child => buildTagNode(child, nextVisitedTagIDs))
            .filter((child): child is KnowledgeBookVisualizationTreeNode => !!child);
        const books = (booksByTagID.get(tag.ID) ?? [])
            .map(option => createBookNode(option, tag.ID));
        const children = [...childTags, ...books];
        if (children.length === 0) return null;
        return {
            children,
            id: `classification:${tag.ID}`,
            kind: 'classification',
            name: tag.Name,
        };
    };

    const tagRoots = (childTagsByParentID.get(null) ?? [])
        .map(tag => buildTagNode(tag, new Set<string>()))
        .filter((tag): tag is KnowledgeBookVisualizationTreeNode => !!tag);
    return [
        ...tagRoots,
        ...rootBooks.map(option => createBookNode(option, 'unclassified')),
    ];
};

/**
 * Serializes a Display configuration to Knowledge View Asset Sets: ordinary entries in priority
 * order, then the fallbacks applicable to the view type. Ordinary entries are written whatever
 * their membership; only All 3D Assets is dropped for a Pano view.
 */
export const toKnowledgeViewAssetSets = (
    configuration: IAssetSetDisplayConfiguration,
    isPano = false,
): IKnowledgeViewAssetSet[] => {
    const all3DKey = normalizeID(configuration.All3DAssetsID);
    const allTagsKey = normalizeID(configuration.AllAssetTagsID);
    const specialKeys = new Set([all3DKey, allTagsKey]);
    const specialEntries = (isPano ? [allTagsKey] : [all3DKey, allTagsKey]).flatMap(key => {
        const entry = configuration.Entries.find(candidate => normalizeID(candidate.AssetSetID) === key);
        return entry ? [entry] : [];
    });
    const orderedEntries = [
        ...configuration.Entries.filter(entry => !specialKeys.has(normalizeID(entry.AssetSetID))),
        ...specialEntries,
    ];

    return orderedEntries.map((entry, index) => {
        const assetSet: IKnowledgeViewAssetSet = {
            ID: entry.AssetSetID,
            Ord: index + 1,
            Hidden: entry.Mode === 'hidden',
        };
        if (entry.Mode === 'color' && entry.Color != null) assetSet.Color = entry.Color;
        if (entry.MaterialID) assetSet.MaterialID = entry.MaterialID;
        return assetSet;
    });
};

export const toDisplayConfiguration = (
    knowledgeView: IKnowledgeViewDetail | null,
    assetSets: readonly IAssetSet[],
): IAssetSetDisplayConfiguration | null => {
    if (!knowledgeView?.ID) return null;
    const facilitySpecials = assetSets.filter(assetSet => !assetSet.KnowledgeBookID);
    const all3DAssets = facilitySpecials.find(assetSet => assetSet.Type === AssetSetType.All3DAssets);
    const allAssetTags = facilitySpecials.find(assetSet => assetSet.Type === AssetSetType.AllAssetTags);
    if (!all3DAssets || !allAssetTags) return null;

    const isPano = knowledgeView.Type === 1;
    const setsByID = new Map(assetSets.map(assetSet => [normalizeID(assetSet.ID), assetSet] as const));
    const specialKeys = new Set([normalizeID(all3DAssets.ID), normalizeID(allAssetTags.ID)]);
    const seen = new Set<string>();
    const entries: IAssetSetDisplayEntry[] = [];
    [...(knowledgeView.AssetSets ?? [])]
        .sort((left, right) => (left.Ord ?? 0) - (right.Ord ?? 0))
        .forEach(entry => {
            const key = normalizeID(entry.ID);
            const assetSet = setsByID.get(key);
            // Ordinary entries load regardless of membership; only the fallbacks are view-type
            // specific, so an empty or 3D-only Asset Set keeps its saved display state in Pano.
            if (!entry.ID || !assetSet || seen.has(key) || specialKeys.has(key) ||
                assetSet.Type === AssetSetType.All3DAssets || assetSet.Type === AssetSetType.AllAssetTags) return;
            seen.add(key);
            entries.push({
                AssetSetID: assetSet.ID,
                Mode: entry.Hidden ? 'hidden' as const : entry.Color != null ? 'color' as const : 'default' as const,
                Color: entry.Color ?? null,
                ...(entry.MaterialID ? { MaterialID: entry.MaterialID } : {}),
                Ord: entries.length + 1,
            });
        });

    const appendSpecial = (assetSet: IAssetSet) => {
        const saved = (knowledgeView.AssetSets ?? []).find(entry => normalizeID(entry.ID) === normalizeID(assetSet.ID));
        entries.push({
            AssetSetID: assetSet.ID,
            // A missing reference fails closed as Hidden. A saved reference keeps its own
            // state: Hidden, Color, or visible with default appearance.
            Mode: !saved ? 'hidden' : saved.Hidden ? 'hidden' : saved.Color != null ? 'color' : 'default',
            Color: saved?.Color ?? null,
            ...(saved?.MaterialID ? { MaterialID: saved.MaterialID } : {}),
            Ord: entries.length + 1,
        });
    };
    if (!isPano) appendSpecial(all3DAssets);
    appendSpecial(allAssetTags);

    return {
        SchemaVersion: 3,
        FacilityID: all3DAssets.FacilityID,
        All3DAssetsID: all3DAssets.ID,
        AllAssetTagsID: allAssetTags.ID,
        Entries: entries,
    };
};
